import type { RefObject } from 'react';
import { BarreAjout } from './BarreAjout';
import { ECRIT_HORS_LIGNE } from '../partage/vocabulaire';
import { useCreer, useFileHorsLigne } from '../partage/useArbre';

/**
 * La barre du bas de la feuille (sortie de `Feuille.tsx`, chantier nettoyage-feuille, ticket 02) :
 * le champ qui ajoute une bulle dans celle-ci et ce qui y a été écrit sans réseau.
 *
 * Au téléphone, plus de champ en bas pour ajouter : c'est le + (chantier
 * `telephone-projets-fiche`, ticket 02, défaut accepté de la décision 20). Ce qui a été écrit hors
 * ligne s'y montre toujours. L'ordinateur garde sa barre.
 */
export function BarreDeLaFeuille({
  id,
  titre,
  couleur,
  vive,
  champ,
  auPouce,
  onEcrite,
}: {
  /** La bulle de la feuille, où l'on ajoute. */
  id: string;
  titre: string;
  couleur: string | null;
  /** Une feuille vide : la barre appelle. */
  vive: boolean;
  /** Le champ, pour que « + La première chose à faire » y pose le curseur. */
  champ: RefObject<HTMLInputElement | null>;
  auPouce: boolean;
  /**
   * Une bulle écrite et créée (pas mise en file) : Paramètres dit « Ouvrir sa fiche » (chantier
   * modes-de-la-carte, modification 04), la feuille l'ouvre ; sans lui, on écrit la suivante.
   */
  onEcrite?: (id: string) => void;
}) {
  // Sans réseau, la bulle attend dans la file et se montre ci-dessous.
  const creer = useCreer(id, { file: true });
  const file = useFileHorsLigne();

  /*
   * Ce qui a été capturé ici sans réseau. On le montre à sa place plutôt que de laisser
   * croire que l'idée s'est perdue — c'est tout l'objet de la file (docs/adr/0001).
   */
  const enAttenteIci = (file.data ?? []).filter((c) => c.parentId === id);

  if (auPouce && enAttenteIci.length === 0) return null;
  return (
    <div className="feuille-barre" data-tuto="feuille-barre">
      {enAttenteIci.length > 0 && (
        <ul className="feuille-en-attente" aria-label="capturé hors ligne">
          {enAttenteIci.map((c) => (
            <li key={c.cle}>
              <span className="feuille-en-attente-point" aria-hidden="true" />
              {c.titre}
              <em>{ECRIT_HORS_LIGNE}</em>
            </li>
          ))}
        </ul>
      )}
      {!auPouce && (
        <BarreAjout dans={titre} couleur={couleur} vive={vive} champ={champ} onAjouter={(t) =>
            creer.mutate(t, {
              onSuccess: (creee) => {
                if (onEcrite && 'id' in creee) onEcrite(creee.id);
              },
            })
          }
        />
      )}
    </div>
  );
}
